import { CURRENCY } from 'src/types';

export enum BILLING_TYPE {
    POST = 'post',
    PRE = 'pre',
}

export enum CALCULATION_TYPE {
    PER_FIXED_TICK = 'per_fixed_tick',
    PER_PERIODS_COMPOUND = 'per_periods_compound',
    PER_PERIODS_TOTAL = 'per_periods_total',
    FIXED_PRICE = 'fixed_price',
}

export enum PRICING_PLAN_STATUS {
    DRAFT = 'draft',
    PUBLISHED = 'published',
}

export enum FREE_TIME_TYPE {
    EVERY_VISIT = 'every_visit',
    CAPPED = 'capped',
}

export enum CAPPING {
    NONE = 'none',
    ONE_TIME = 'one_time',
    REPEATING = 'repeating',
}

export enum TICK_TYPE {
    MINUTE = 'minute',
    HOUR = 'hour',
    DAY = 'day',
    WEEK = 'week',
    MONTH = 'month',
}

type AdministrativeTranslation = {
    id: string;
    created_at: string;
    modified_at: string;
    language: string;
    name: string;
    description: string;
};

export type PricingPlan = {
    id: string;
    translations: AdministrativeTranslation[];
    created_at: string;
    modified_at: string;
    billing_type: BILLING_TYPE;
    status: PRICING_PLAN_STATUS;
    calculation_type: CALCULATION_TYPE;
    version_number: number;
    valid_from: string;
    valid_to: string;
    tax_percent: string;
    currency: CURRENCY;
    free_time_type: FREE_TIME_TYPE;
    free_time_cap_minutes: number | null;
    free_time_minutes: number | null;
    exit_time: number | null;
    exit_time_grace: number | null;
    capping: CAPPING;
    is_current_version: boolean;
    is_system: boolean;
    identity: string;
    previous_version: PricingVersion | null;
    next_version: PricingVersion | null;
    tick: {
        tick_type: TICK_TYPE;
        tick_minutes: number;
    };
    fixed_rate?: {
        rate_with_tax: number;
    };
    extra_free_time: boolean | null;
    extra_free_time_minutes: number | null;
    extra_free_time_activation_remaining_minutes: number | null;
};

export enum PAYMENT_TYPE {
    CARD = 'card',
    BANK = 'bank',
    CASH = 'cash',
    WEB = 'web',
}

export type PricingPlanPeriodAdjustment = {
    id: string;
    created_at: string;
    modified_at: string;
    payment_type: PAYMENT_TYPE;
    valid_from: string;
    valid_to: string | null;
    day_of_week: DAY_OF_WEEK | null;
    from_time: string | null;
    to_time: string | null;
    adjustment_percent: string | null;
    adjustment_amount: number | null;
};

export enum DAY_OF_WEEK {
    MONDAY = 'monday',
    TUESDAY = 'tuesday',
    WEDNESDAY = 'wednesday',
    THURSDAY = 'thursday',
    FRIDAY = 'friday',
    SATURDAY = 'saturday',
    SUNDAY = 'sunday',
}

export type PricingVersion = {
    id: string;
    version_number: number;
    valid_from: string;
    valid_to: string | null;
};

export type PricingPlanTickRate = {
    id: string;
    created_at: string;
    modified_at: string;
    // The band runs from start_day at from_time to end_day at to_time - it may span several days
    start_day: DAY_OF_WEEK;
    end_day: DAY_OF_WEEK;
    from_time: string;
    to_time: string;
    rate_with_tax: number;
    // The most the rate charges within its time range, in cents; null - no limit
    max_amount: number | null;
    pricing_plan: string;
    pricing_plan_tick: string;
    currency: CURRENCY;
};

export type PricingPlanCap = {
    id: string;
    created_at: string;
    modified_at: string;
    cap_type: TICK_TYPE;
    cap_minutes: number;
    rate_with_tax: number;
};

export type PricingPlanTier = {
    id: string;
    order: number;
    created_at: string;
    modified_at: string;
    start_minute: number;
    end_minute: number | null;
    rate_with_tax: number;
    tick_type: TICK_TYPE;
    tick_minutes: number;
};

export interface PricingPlanUsedInColumns {
    type: LINKED_ENTITIES_KEYS;
    name?: string;
    id?: string;
    valid_to?: string | null;
    valid_from?: string;
    active_stays_count?: number;
    active_memberships_count?: number;
}

export enum LINKED_ENTITIES_KEYS {
    SITE_PRICING_PLANS = 'site_pricing_plans',
    GATEWAY_PRICING_PLANS = 'gateway_pricing_plans',
    ZONE_PRICING_PLANS = 'zone_pricing_plans',
    MEMBERSHIPS = 'memberships',
    MEMBERSHIP_TYPES = 'membership_types',
}

export type UsedInMembership = {
    id: string;
    name: string;
    valid_from: string;
    valid_to?: string | null;
    active_stays_count: number;
};
export type UsedInMembershipType = {
    id: string;
    active_memberships_count: number;
    translations: {
        language: string;
        name: string;
    }[];
};

export type PricingPlanUsedIn = {
    total_entities_count: number;
    [LINKED_ENTITIES_KEYS.ZONE_PRICING_PLANS]: {
        total_count: number;
        zone_pricing_plans: {
            id: string;
            valid_from: string;
            valid_to?: string | null;
            active_stays_count: number;
            zone: {
                id: string;
                translations: {
                    language: string;
                    name: string;
                }[];
            };
        }[];
    };
    [LINKED_ENTITIES_KEYS.SITE_PRICING_PLANS]: {
        total_count: number;
        site_pricing_plans: {
            id: string;
            valid_from: string;
            valid_to?: string | null;
            active_stays_count: number;
            site: {
                id: string;
                translations: {
                    language: string;
                    name: string;
                }[];
            };
        }[];
    };
    [LINKED_ENTITIES_KEYS.GATEWAY_PRICING_PLANS]: {
        total_count: number;
        gateway_pricing_plans: {
            id: string;
            valid_from: string;
            valid_to?: string | null;
            active_stays_count: number;
            gateway: {
                id: string;
                translations: {
                    language: string;
                    name: string;
                }[];
            };
        }[];
    };
    [LINKED_ENTITIES_KEYS.MEMBERSHIPS]: {
        total_count: number;
        memberships: UsedInMembership[];
    };
    [LINKED_ENTITIES_KEYS.MEMBERSHIP_TYPES]: {
        total_count: number;
        membership_types: UsedInMembershipType[];
    };
};

export type TransformedLinkedEntities = {
    total_entities_count: number;
    total_active_stays: number;
    total_active_memberships: number;
    transformedData: PricingPlanUsedInColumns[];
};

// The pricing simulation of the pricing plan dashboard - a read-only breakdown of what a stay would be charged
export enum PRICING_SIMULATION_ROW_KIND {
    FREE_TIME = 'free_time',
    CHARGED = 'charged',
    UNCHARGED = 'uncharged',
    CAPPED = 'capped',
    // No version of the tariff is valid then - a real stay is not charged by it
    NO_PLAN = 'no_plan',
}

export type PricingSimulationTickRate = {
    id: string;
    start_day: DAY_OF_WEEK;
    from_time: string;
    end_day: DAY_OF_WEEK;
    to_time: string;
    rate_with_tax: number;
    max_amount: number | null;
};

export enum PRICING_SIMULATION_CONTEXT {
    PLAN = 'plan',
    GATEWAY = 'gateway',
    ZONE = 'zone',
    SITE = 'site',
    MEMBERSHIP_TYPE = 'membership_type',
}

export type PricingSimulationPlan = {
    id: string;
    identity: string;
    version_number: number;
    calculation_type: CALCULATION_TYPE;
    translations: { language: string; name: string }[];
};

export type PricingSimulationTier = {
    id: string;
    start_minute: number;
    end_minute: number | null;
    tick_type: TICK_TYPE;
    tick_minutes: number;
    rate_with_tax: number;
};

export type PricingSimulationRow = {
    kind: PRICING_SIMULATION_ROW_KIND;
    start: string;
    end: string;
    // Both in cents, with tax: what this row charges and what the stay owes up to and including it
    amount_with_tax: number;
    total_with_tax: number;
    tick_rate: PricingSimulationTickRate | null;
    tier: PricingSimulationTier | null;
    // Which tariff priced the row - another one takes over where it is the one that applies
    pricing_plan: PricingSimulationPlan | null;
};

export type PricingSimulation = {
    calculation_type: CALCULATION_TYPE;
    currency: CURRENCY;
    tax_percent: string;
    tick_minutes: number | null;
    start: string;
    end: string;
    total_with_tax: number;
    total_amount: number;
    total_tax_amount: number;
    rows: PricingSimulationRow[];
};
